import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Clock, Star } from 'lucide-react';
import { useDailyBonus } from '@/hooks/useDailyBonus';
import { useNavigate } from 'react-router-dom';

const DailyBonusNotification: React.FC = () => {
  const { canClaimDaily, timeUntilNextClaim, claimDailyBonus, isClaimingDaily, isLoading } = useDailyBonus();
  const navigate = useNavigate();

  // Don't show claimable state until we've verified eligibility
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-r from-yellow-500/90 to-orange-500/90 border-2 border-yellow-400 mb-6 shadow-xl overflow-hidden">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white rounded-full shadow-lg flex-shrink-0">
              <Gift className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-black font-bold text-lg">Loading bonus status...</h3>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleClaimBonus = async () => {
    await claimDailyBonus();
  };

  const handleViewPoints = () => {
    navigate('/points');
  };

  return (
    <Card className="bg-gradient-to-r from-yellow-500/90 to-orange-500/90 border-2 border-yellow-400 mb-6 shadow-xl overflow-hidden">
      <CardContent className="p-4 md:p-6">
        {/* Mobile Layout: Vertical Stack */}
        <div className="flex flex-col space-y-4 md:hidden">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white rounded-full shadow-lg flex-shrink-0">
              <Gift className="h-6 w-6 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              {canClaimDaily ? (
                <>
                  <h3 className="text-black font-bold text-lg">🎁 Daily Bonus Available!</h3>
                  <p className="text-black/80 text-sm font-semibold">
                    Claim your daily 10 points
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-black font-bold text-lg">✅ Daily Bonus Claimed!</h3>
                  <p className="text-black/80 text-sm font-semibold">
                    Next in {timeUntilNextClaim}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col space-y-2 w-full">
            {canClaimDaily && (
              <Button
                onClick={handleClaimBonus}
                disabled={isClaimingDaily}
                className="bg-green-600 text-white hover:bg-green-700 font-bold shadow-lg text-sm py-2 w-full"
              >
                {isClaimingDaily ? (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 mr-2" />
                    Claim Bonus
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleViewPoints}
              className="border-2 border-black bg-white text-black hover:bg-gray-100 font-bold text-sm py-2 w-full"
            >
              View Points
            </Button>
          </div>
        </div>

        {/* Desktop Layout: Horizontal */}
        <div className="hidden md:flex md:items-center md:justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-white rounded-full shadow-lg">
              <Gift className="h-7 w-7 text-orange-600" />
            </div>
            <div>
              {canClaimDaily ? (
                <>
                  <h3 className="text-black font-bold text-xl">🎁 Daily Bonus Available!</h3>
                  <p className="text-black/80 text-base font-semibold">
                    Claim your daily 10 points and climb the leaderboard
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-black font-bold text-xl">✅ Daily Bonus Claimed!</h3>
                  <p className="text-black/80 text-base font-semibold">
                    Next bonus available in {timeUntilNextClaim} • View your points anytime
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            {canClaimDaily && (
              <Button
                onClick={handleClaimBonus}
                disabled={isClaimingDaily}
                className="bg-green-600 text-white hover:bg-green-700 font-bold shadow-lg text-base px-6 py-3"
              >
                {isClaimingDaily ? (
                  <>
                    <Clock className="h-5 w-5 mr-2" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Star className="h-5 w-5 mr-2" />
                    Claim +10 pts
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleViewPoints}
              className="border-2 border-black bg-white text-black hover:bg-gray-100 font-bold text-base px-6 py-3"
            >
              View Points
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyBonusNotification;